import { FIELD, type MatchState } from "@golazo/shared";
import { lerp } from "../core/math.js";
import { replayProgress } from "../game/replay.js";
import { JUICE } from "./config.js";

export interface CameraJuice {
  /** Dispara el acercamiento hacia un punto del mundo (coords de campo, sin margen). */
  focusGoal(worldX: number, worldY: number): void;
  /** Sacudida de zoom al soltar un tiro cargado. */
  punchShot(power: number): void;
  resize(): void;
  update(dt: number, match: MatchState): void;
  /** Multiplicador de zoom sobre el encuadre de juego. */
  readonly zoom: number;
  /**
   * 0 = manda la camara de juego, 1 = manda el punto del gol. Quien compone
   * la camara mezcla entre los dos con este peso.
   */
  readonly focusWeight: number;
  /** Punto del gol, en coordenadas de campo (sin el margen de estadio). */
  readonly focusX: number;
  readonly focusY: number;
}

/**
 * Zoom cinematico al marcar. No mueve la camara el mismo: expone a donde
 * quiere mirar y cuanto manda, y `match.ts` lo mezcla con la camara de juego.
 *
 * Antes calculaba aqui su propio `panX/panY` suponiendo que la camara normal
 * estaba centrada en todo el campo. Con la camara de seguimiento eso dejo de
 * ser cierto, y dos sitios calculando la misma transformada con supuestos
 * distintos es la receta para que se peleen.
 */
export function createCameraJuice(): CameraJuice {
  let focusX = FIELD.WIDTH / 2;
  let focusY = FIELD.HEIGHT / 2;
  let focusT = 0;
  let phase: "idle" | "in" | "hold" | "out" = "idle";
  let timer = 0;
  let shotPunch = 0;
  /** Acercamiento de la repeticion, suavizado para que no arranque de golpe. */
  let replayZoomT = 0;

  return {
    focusGoal(worldX: number, worldY: number): void {
      focusX = worldX;
      focusY = worldY;
      focusT = 0;
      phase = "in";
      timer = 0;
    },

    punchShot(power: number): void {
      shotPunch = Math.max(shotPunch, JUICE.CAMERA.SHOT_PUNCH * (0.55 + power * 0.45));
    },

    resize(): void {
      // La camara del gol ya no depende del tamaño de pantalla; quien compone
      // la transformada (match.ts) lo lee fresco en cada frame.
    },

    update(dt: number, match: MatchState): void {
      if (shotPunch > 0) {
        shotPunch = Math.max(0, shotPunch - dt * JUICE.CAMERA.SHOT_PUNCH_DECAY);
      }
      /*
       * El acercamiento de la repeticion va aparte del zoom del gol: aquel
       * salta al punto donde entro el balon y vuelve, este es un empuje
       * continuo que dura todo el clip. Se persigue el objetivo en vez de
       * copiarlo para que la entrada y la salida sean suaves aunque el clip
       * arranque de golpe.
       */
      const replayTarget = replayProgress(match);
      replayZoomT += (replayTarget - replayZoomT) * Math.min(1, dt * 3.2);

      if (phase === "idle") return;

      timer += dt;
      const replaying = replayTarget > 0;

      if (phase === "in") {
        focusT = Math.min(1, timer / JUICE.CAMERA.GOAL_ZOOM_IN);
        if (focusT >= 1) {
          phase = "hold";
          timer = 0;
        }
      } else if (phase === "hold") {
        focusT = 1;
        // Celebración terminada: abrir plano para la repetición o fin de pausa.
        if (replaying || match.phase !== "goal") {
          phase = "out";
          timer = 0;
        }
      } else if (phase === "out") {
        const t = Math.min(1, timer / JUICE.CAMERA.GOAL_ZOOM_OUT);
        focusT = 1 - t;
        if (t >= 1) phase = "idle";
      }
    },

    get zoom(): number {
      const target = JUICE.CAMERA.GOAL_ZOOM;
      const eased = phase === "idle" ? 0 : 1 - (1 - focusT) ** 3;
      const goalZoom = lerp(1, target, eased);
      const punch = 1 + shotPunch;
      const replayZoom = lerp(1, JUICE.CAMERA.REPLAY_ZOOM, replayZoomT);
      return goalZoom * punch * replayZoom;
    },

    get focusWeight(): number {
      return focusT;
    },

    get focusX(): number {
      return focusX;
    },

    get focusY(): number {
      return focusY;
    },
  };
}
